import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import modelsData from './data/models.json';
import { getRelativeTime } from './utils/timeUtils';
import './DemoHub.css';

const DemoHub = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTask, setSelectedTask] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  // Hardware filter is hidden but we keep the state for compatibility
  const [selectedHardware, setSelectedHardware] = useState('all');

  // Task icons
  const getTaskIcon = (task, isFilter = false) => {
    const iconClass = isFilter ? "filter-task-icon" : "task-icon";
    const iconSize = isFilter ? "20" : "55";
    
    switch (task) {
      case 'translation':
        return (
          <div className={iconClass}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'sentiment':
        return (
          <div className={iconClass}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'tts':
        return (
          <div className={iconClass}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 010 7.07M19 5a9 9 0 010 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'stt':
        return (
          <div className={iconClass}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'all':
        return (
          <div className={iconClass}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" fill="#6B7280"/>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  // Language icons
  const getLanguageIcon = (language) => {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c.43 1.43 1.08 2.76 1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.35-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" fill="currentColor"/>
      </svg>
    );
  };

  // Hardware icons
  const getHardwareIcon = (hardware) => {
    switch (hardware) {
      case 'cpu':
        return (
          <div className="hardware-icon cpu-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="16" height="16" rx="2" fill="#E5E7EB" stroke="#6B7280" strokeWidth="1"/>
              <rect x="7" y="7" width="10" height="10" rx="1" fill="#60A5FA" stroke="#2563EB" strokeWidth="1"/>
              <circle cx="12" cy="12" r="3" fill="#2563EB"/>
              <rect x="2" y="8" width="2" height="2" fill="#6B7280"/>
              <rect x="2" y="14" width="2" height="2" fill="#6B7280"/>
              <rect x="20" y="8" width="2" height="2" fill="#6B7280"/>
              <rect x="20" y="14" width="2" height="2" fill="#6B7280"/>
              <rect x="8" y="2" width="2" height="2" fill="#6B7280"/>
              <rect x="14" y="2" width="2" height="2" fill="#6B7280"/>
              <rect x="8" y="20" width="2" height="2" fill="#6B7280"/>
              <rect x="14" y="20" width="2" height="2" fill="#6B7280"/>
            </svg>
          </div>
        );
      case 'gpu':
        return (
          <div className="hardware-icon gpu-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="16" height="16" rx="2" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="1"/>
              <rect x="7" y="7" width="10" height="10" rx="1" fill="#FBBF24" stroke="#F59E0B" strokeWidth="1"/>
              <circle cx="12" cy="12" r="3" fill="#F59E0B"/>
              <rect x="2" y="8" width="2" height="2" fill="#F59E0B"/>
              <rect x="2" y="14" width="2" height="2" fill="#F59E0B"/>
              <rect x="20" y="8" width="2" height="2" fill="#F59E0B"/>
              <rect x="20" y="14" width="2" height="2" fill="#F59E0B"/>
              <rect x="8" y="2" width="2" height="2" fill="#F59E0B"/>
              <rect x="14" y="2" width="2" height="2" fill="#F59E0B"/>
              <rect x="8" y="20" width="2" height="2" fill="#F59E0B"/>
              <rect x="14" y="20" width="2" height="2" fill="#F59E0B"/>
            </svg>
          </div>
        );
      case 'all':
        return (
          <div className="hardware-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="16" height="16" rx="4" fill="#9CA3AF" stroke="#6B7280" strokeWidth="1.5"/>
              <rect x="9" y="9" width="6" height="6" rx="1.5" fill="#6B7280"/>
              <circle cx="8" cy="8" r="1.5" fill="#FBBF24"/>
              <circle cx="16" cy="8" r="1.5" fill="#9CA3AF"/>
              <circle cx="8" cy="16" r="1.5" fill="#9CA3AF"/>
              <circle cx="16" cy="16" r="1.5" fill="#FBBF24"/>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  // Load models from JSON and add icons
  const demos = modelsData.models.map(model => ({
    ...model,
    icon: getTaskIcon(model.tags.includes('translation') ? 'translation' : 
                     model.tags.includes('sentiment') ? 'sentiment' :
                     model.tags.includes('tts') ? 'tts' : 'stt'),
    hardwareIcon: getHardwareIcon(model.hardware),
    bgColor: model.bgColor
  }));

  const demoColors = {
    translation: '#4F46E5',
    sentiment: '#059669',
    tts: '#DC2626',
    stt: '#7C3AED',
    support: '#EA580C'
  };

  const taskFilters = [
    { key: 'all', label: 'All Tasks', count: 0 },
    { key: 'translation', label: 'Translation', count: 0 },
    // Hiding sentiment analysis filter
    { key: 'sentiment', label: 'Sentiment Analysis', count: 0 },
    { key: 'tts', label: 'Text-to-Speech', count: 0 },
    { key: 'stt', label: 'Speech-to-Text', count: 0 }
  ];

  const languageFilters = [
    { key: 'all', label: 'All Languages', count: 0 },
    { key: 'tunisian_arabic', label: 'Tunisian Arabic', count: 0 },
    { key: 'moroccan_arabic', label: 'Moroccan Arabic', count: 0 },
    { key: 'amazigh', label: 'Amazigh', count: 0 }
  ];

  const hardwareFilters = [
    { key: 'all', label: 'All Hardware', count: 0 },
    { key: 'cpu', label: 'CPU', count: 0 },
    { key: 'gpu', label: 'GPU', count: 0 }
  ];

  // Handle demo click navigation
  const handleDemoClick = (demo) => {
    // Special-case: Swahili ↔ French demo keeps its own route
    if (demo.id === 'swahili-french-translator') {
      navigate('/demo/swahili-french-translator');
      return;
    }
    if (demo.id === 'swahili-french-conversation') {
      navigate('/demo/swahili-french-conversation');
      return;
    }

    // Extract base demo ID by removing language suffix
    let baseId = demo.id;
    
    // Remove language suffixes to get the base demo type
    if (demo.id.includes('-tn')) {
      baseId = demo.id.replace('-tn', '');
    } else if (demo.id.includes('-ma')) {
      baseId = demo.id.replace('-ma', '');
    } else if (demo.id.includes('-eg')) {
      baseId = demo.id.replace('-eg', '');
    }
    else if (demo.id.includes('-amazigh')) {
      baseId = demo.id.replace('-amazigh', '');
    }
    
    // Route all translation demos to the generic translator page
    if (baseId === 'darija-translator' || baseId === 'amazigh-translator' || demo.tags.includes('translation')) {
      baseId = 'translator';
    }
    
    // Route all speech-to-text demos to the generic speech-to-text page
    if (demo.tags.includes('stt')) {
      baseId = 'speech-to-text';
    }
    
    if (demo.language && demo.language !== 'all') {
      navigate(`/demo/${baseId}?language=${demo.language}`);
    } else {
      navigate(`/demo/${baseId}`);
    }
  };

  const FilterSection = ({ title, filters, selected, onSelect, getIcon }) => {
    return (
      <div className="filter-section">
        <h3 className="filter-title">{title}</h3>
        <div className="filter-options">
          {filters.map(filter => {
            const isSelected = filter.key === selected;
            
            return (
              <button
                key={filter.key}
                onClick={() => onSelect(filter.key)}
                className={`filter-option ${isSelected ? 'active' : ''}`}
              >
                {getIcon && getIcon(filter.key, true)}
                <span className="filter-option-label">{filter.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const getTaskColor = (key) => {
    switch (key) {
      case 'translation':
        return { text: '#4F46E5', border: '#D1D5DB' };
      case 'sentiment':
        return { text: '#059669', border: '#D1D5DB' };
      case 'tts':
        return { text: '#DC2626', border: '#D1D5DB' };
      case 'stt':
        return { text: '#7C3AED', border: '#D1D5DB' };
      default:
        return { text: '#6B7280', border: '#E5E7EB' };
    }
  };

  const getLanguageColor = (key) => {
    switch (key) {
      case 'tunisian_arabic':
        return { text: '#059669', border: '#D1D5DB' };
      case 'moroccan_arabic':
        return { text: '#059669', border: '#D1D5DB' };
      case 'amazigh':
        return { text: '#059669', border: '#D1D5DB' };
      default:
        return { text: '#6B7280', border: '#E5E7EB' };
    }
  };

  const getHardwareColor = (key) => {
    switch (key) {
      case 'cpu':
        return { text: '#6B7280', border: '#D1D5DB' };
      case 'gpu':
        return { text: '#F97316', border: '#D1D5DB' };
      default:
        return { text: '#6B7280', border: '#E5E7EB' };
    }
  };

  // Calculate filter counts and sort by updated recently
  const filteredDemos = useMemo(() => {
    const filtered = demos.filter(demo => {
      // Hide sentiment analysis models
      //if (demo.tags.includes('sentiment')) {
       // return false;
      //}
      
      const matchesSearch = demo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           demo.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTask = selectedTask === 'all' || demo.tags.includes(selectedTask);
      const matchesLanguage = selectedLanguage === 'all' || demo.tags.includes(selectedLanguage) || demo.language === selectedLanguage;
      const matchesHardware = selectedHardware === 'all' || demo.hardware === selectedHardware;
      
      return matchesSearch && matchesTask && matchesLanguage && matchesHardware;
    });
    
    // Sort by most recently updated by default
    return filtered.sort((a, b) => {
      // Convert time strings like "4h ago", "1d ago" to comparable values
      // Lower value = more recent
      const getTimeValue = (timeStr) => {
        if (!timeStr) return 9999; // If no time, put at the end
        
        const hours = timeStr.includes('h') ? parseInt(timeStr) : 
                     timeStr.includes('d') ? parseInt(timeStr) * 24 : 9999;
        return hours;
      };
      
      return getTimeValue(a.lastUpdated) - getTimeValue(b.lastUpdated);
    });
  }, [demos, searchTerm, selectedTask, selectedLanguage, selectedHardware]);

  // Update filter counts
  const updateFilterCounts = (filters, filterType) => {
    return filters.map(filter => {
      if (filter.key === 'all') {
        return { ...filter, count: filteredDemos.length };
      }
      
      let count = 0;
      if (filterType === 'task') {
        count = demos.filter(demo => demo.tags.includes(filter.key)).length;
      } else if (filterType === 'language') {
        count = demos.filter(demo => demo.tags.includes(filter.key) || demo.language === filter.key).length;
      } else if (filterType === 'hardware') {
        count = demos.filter(demo => demo.hardware === filter.key).length;
      }
      
      return { ...filter, count };
    });
  };

  const tasksWithCounts = updateFilterCounts(taskFilters, 'task');
  const languagesWithCounts = updateFilterCounts(languageFilters, 'language');
  const hardwareWithCounts = updateFilterCounts(hardwareFilters, 'hardware');

  return (
    <div className="demo-hub-page">
      <div className="demo-hub-content">
        <div className="demo-hub-container">
          <div className="demo-hub-layout">
            {/* Sidebar */}
            <div>
              <FilterSection
                title="Task"
                filters={tasksWithCounts}
                selected={selectedTask}
                onSelect={setSelectedTask}
                getIcon={getTaskIcon}
              />
              <FilterSection
                title="Language"
                filters={languagesWithCounts}
                selected={selectedLanguage}
                onSelect={setSelectedLanguage}
                getIcon={getLanguageIcon}
              />
              {/* Hardware filters hidden as requested */}
            </div>

            {/* Main Content */}
            <div>
              <div className="models-header">
                <h2 className="models-title">
                  Models
                </h2>
                <div className="models-meta">
                  <span className="models-count">{filteredDemos.length} models</span>
                  {/* Comment this for now */}
                  {/* <div className="models-filter-badge">
                    Updated recently
                  </div> */}
                </div>
              </div>

              {/* Search Bar */}
              <div className="search-container">
                <div className="search-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search models..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {/* Models Grid */}
              {filteredDemos.length > 0 ? (
                <div className="models-grid">
                  {filteredDemos.map((demo, index) => (
                    <div
                      key={index}
                      onClick={() => handleDemoClick(demo)}
                      className="model-card"
                    >
                      {/* Header with icon and title */}
                      <div className="card-header">
                        <div className="task-icon-container">
                          {demo.icon}
                          {/* Country flag positioned at bottom right of task icon */}
                          {demo.flag && (
                            <span className={`fi fi-${demo.flag.toLowerCase()} country-flag-overlay`} />
                          )}
                        </div>
                        <div className="title-language-container">
                          <h3 className="model-title">
                            {demo.title}
                          </h3>
                          
                          {/* Language name only (flag is now on the icon) */}
                          {demo.flag && (
                            <div className="language-section">
                              <span className="language-name">
                                {demo.flag === 'tn' ? 'Tunisian Arabic' : 
                                 demo.flag === 'ma' ? 'Moroccan Arabic' : 
                                 demo.flag === 'eg' ? 'Egyptian Arabic' : 
                                 demo.flag === 'cd' ? 'Lingala' : 
                                 demo.flag === 'ml' ? 'Bambara' : 
                                 demo.flag === 'amazigh' ? 'Amazigh' : demo.flag.toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Tags row */}
                      <div className="tags-row">
                        
                        {/* Hardware icon and text */}
                        <div className="hardware-tag">
                          <div className={`hardware-icon ${demo.hardware === 'gpu' ? 'gpu-icon' : 'cpu-icon'}`}>
                            {demo.hardware === 'gpu' ? (
                              // GPU Icon - Modern Design
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="4" y="4" width="16" height="16" rx="2" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="1"/>
                                <rect x="7" y="7" width="10" height="10" rx="1" fill="#FBBF24" stroke="#F59E0B" strokeWidth="1"/>
                                <circle cx="12" cy="12" r="3" fill="#F59E0B"/>
                                <rect x="2" y="8" width="2" height="2" fill="#F59E0B"/>
                                <rect x="2" y="14" width="2" height="2" fill="#F59E0B"/>
                                <rect x="20" y="8" width="2" height="2" fill="#F59E0B"/>
                                <rect x="20" y="14" width="2" height="2" fill="#F59E0B"/>
                                <rect x="8" y="2" width="2" height="2" fill="#F59E0B"/>
                                <rect x="14" y="2" width="2" height="2" fill="#F59E0B"/>
                                <rect x="8" y="20" width="2" height="2" fill="#F59E0B"/>
                                <rect x="14" y="20" width="2" height="2" fill="#F59E0B"/>
                              </svg>
                            ) : (
                              // CPU Icon - Modern Design
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="4" y="4" width="16" height="16" rx="2" fill="#E5E7EB" stroke="#6B7280" strokeWidth="1"/>
                                <rect x="7" y="7" width="10" height="10" rx="1" fill="#60A5FA" stroke="#2563EB" strokeWidth="1"/>
                                <circle cx="12" cy="12" r="3" fill="#2563EB"/>
                                <rect x="2" y="8" width="2" height="2" fill="#6B7280"/>
                                <rect x="2" y="14" width="2" height="2" fill="#6B7280"/>
                                <rect x="20" y="8" width="2" height="2" fill="#6B7280"/>
                                <rect x="20" y="14" width="2" height="2" fill="#6B7280"/>
                                <rect x="8" y="2" width="2" height="2" fill="#6B7280"/>
                                <rect x="14" y="2" width="2" height="2" fill="#6B7280"/>
                                <rect x="8" y="20" width="2" height="2" fill="#6B7280"/>
                                <rect x="14" y="20" width="2" height="2" fill="#6B7280"/>
                              </svg>
                            )}
                          </div>
                          <span className="hardware-label">Running on {demo.hardware.toUpperCase()}</span>
                        </div>
                        
                        <div className="separator-dot">•</div>
                        
                        {/* Version text */}
                        <div className="version-tag">
                          {demo.version || 'v0.9'}
                        </div>
                        
                        <div className="separator-dot">•</div>
                        
                        <div className="update-time">
                          {demo.lastUpdated ? `Updated ${getRelativeTime(demo.lastUpdated)}` : 'Updated 4h ago'}
                        </div>
                      </div>

                      {/* Description */}
                      <p className="model-description">
                        {demo.description}
                      </p>

                      {/* Hover icon indicator for clickable card */}
                      <div data-hover-icon className="hover-arrow-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="M21 21l-4.35-4.35"/>
                    </svg>
                  </div>
                  <h3 className="no-results-title">
                    No models found
                  </h3>
                  <p className="no-results-message">Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoHub;
